/**
 * React-19 widget renderer — the OPT-IN half of the host seam in `./widgets`.
 *
 * `ReactDOM.render` / `unmountComponentAtNode` were removed in React 19, so a React-19 host
 * (slate) hands this to `EditorV2`'s `widgetRenderer` prop, or straight to
 * `buildWidgetNodeViews(renderer)`. It lives in its OWN module, imported by path and never
 * re-exported from `src/index.ts`, so `react-dom/client` never enters knowit-main's React-17
 * module graph — one package, two hosts, no fork (slate-feature-transfer W6 §C4 / D9).
 *
 * One root per NodeView element, kept in a WeakMap: `mount` runs again for the same element on
 * every attribute change (a remote peer editing the payload), and calling `createRoot` twice on
 * one container is a React error.
 */

import type * as React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { WidgetRenderer } from './widgets';

export function createReact19WidgetRenderer(): WidgetRenderer {
  const roots = new WeakMap<HTMLElement, Root>();

  return {
    mount(el: HTMLElement, node: React.ReactNode): void {
      let root = roots.get(el);
      if (!root) {
        root = createRoot(el);
        roots.set(el, root);
      }
      root.render(node);
    },
    unmount(el: HTMLElement): void {
      const root = roots.get(el);
      if (!root) {
        return;
      }
      roots.delete(el);
      // ProseMirror destroys a NodeView from inside its own update cycle, and unmounting a
      // root synchronously while React is rendering warns loudly. A microtask defers it past
      // the current commit without leaking the root.
      queueMicrotask(() => root.unmount());
    },
  };
}
